import React from "react";
import bg from "../assets/about3.png";
import { Link, useNavigate } from "react-router-dom";
import GenericApi from "@/api/genericApi";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import { ChangePasswordRequest, ChangePasswordRequestSchema } from "@/types/Auth";
import { FormError, ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { formatError } from "@/utils/formatError";
import { AxiosError, isAxiosError } from "axios";
import FormField from "@/components/CustomForm/FormField";
import { FormFieldType } from "@/types/FormFieldsType";
import {useSnackBarAlert} from "@/hooks/useSnackbar.tsx";

const ChangePassword: React.FC = () => {

    const navigate = useNavigate();
    const { showSnackBar } = useSnackBarAlert();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<ChangePasswordRequest>({
        resolver: zodResolver(ChangePasswordRequestSchema),
    });


    const changePasswordMutation = useMutation<
        ApiResponse<null>,
        ApiError<ValidationErrors>,
        ChangePasswordRequest
    >({
        mutationFn: (data) =>
            GenericApi.put<null>(ApiRoutes.CHANGE_PASSWORD, {
                ...data,
            }),
        onSuccess: async () => {
            CustomLogger.log("Password changed successfully.");
            showSnackBar("Password changed successfully.","success",{
                vertical: 'top',
                horizontal: 'right'
            }, 5000)
            navigate("/dashboard", { replace: true });
        },
        onError: async ({ error }) => {
            CustomLogger.error(error);
            handleAxiosError(error);
            showSnackBar("An error occurred","error",{
                vertical: 'top',
                horizontal: 'right'
            }, 5000)
        },
    });

    const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
        if (isAxiosError<FormError>(error)) {
            setError("root", {
                type: "manual",
                message:
                    error.response?.data?.detail ||
                    "An error occurred while changing the password",
            });
            return;
        }

        if (isAxiosError<ValidationErrors>(error)) {
            setError("root", {
                type: "manual",
                message: formatError(error, "An error occurred while changing the password"),
            });
            return;
        }

        setError("root", {
            type: "manual",
            message: error?.message || "An error occurred while changing the password",
        });
    };

    const onSubmit = async (data: ChangePasswordRequest) => {
        changePasswordMutation.mutate(data);
    };

    return (
        <div className="login">
            <img draggable="false" className="login__bg__img" src={bg} alt="bg" />
            <div className="login__container !w-[50%]">
                <div className="login__container__top flex items-center">
                    <Link to="/dashboard" className="login__container__top__arrow">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="24"
                            viewBox="0 0 15 24"
                            fill="none"
                        >
                            <path
                                d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                                stroke="#05004E"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                    </Link>
                    <div className='ml-3 text-5xl w-full'>Change Password</div>
                </div>
                <form
                    className="login__container__fields"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <FormField<ChangePasswordRequest>
                        type={FormFieldType.PASSWORD}
                        placeholder="Current Password"
                        name="old_password"
                        register={register}
                        error={errors.old_password}
                        label="Current Password"
                        valueAsNumber={false}
                        required={true}
                        labelStyle="login__container__fields__title"
                        inputStyle="login__container__fields__space"
                    />
                    <FormField<ChangePasswordRequest>
                        type={FormFieldType.PASSWORD}
                        placeholder="New Password"
                        name="new_password"
                        register={register}
                        error={errors.new_password}
                        label="New Password"
                        valueAsNumber={false}
                        required={true}
                        labelStyle="login__container__fields__title"
                        inputStyle="login__container__fields__space"
                    />
                    <FormField<ChangePasswordRequest>
                        type={FormFieldType.PASSWORD}
                        placeholder="Confirm New Password"
                        name="confirm_password"
                        register={register}
                        error={errors.confirm_password}
                        label="Confirm New Password"
                        valueAsNumber={false}
                        required={true}
                        labelStyle="login__container__fields__title"
                        inputStyle="login__container__fields__space"
                    />
                    <button type="submit" className="button--blue">
                        {changePasswordMutation.isPending ? "Loading..." : "Change Password"}
                    </button>
                </form>
                {changePasswordMutation.isError && (
                    <span className="block text-red-500 pt-4 sm:inline">
                        {errors.root?.message}
                    </span>
                )}
                <div className="login__container__switch mt-4">
                    <div className="login__container__switch__text">
                        Remembered your password?
                    </div>
                    <Link
                        to="/login"
                        className="login__container__switch__link"
                    >
                        Log In
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
